import {AbstractInputComponent} from "../abstract-input.component";
import {customElement} from "lit/decorators/custom-element.js";
import {UuidHelper} from "../../../helpers/uuid.helper";
import {property} from "lit/decorators/property.js";
import {html, HTMLTemplateResult} from "lit";
import {RadioButtonItem} from "../ha-input-checkbox/radio-button-item.interface";
import {ArrayHelper} from "../../../helpers/array.helper";
import {when} from "lit/directives/when.js";
import {repeat} from "lit/directives/repeat.js";
import {state} from "lit/decorators/state.js";

@customElement(`ha-input-radio-group`)
export class HaInputRadioGroupComponent<T = unknown> extends AbstractInputComponent {

    private _id = UuidHelper.generateV7();

    private _items: RadioButtonItem[] = [];

    @property({type: Array})
    public get items(): RadioButtonItem[] {
        return this._items;
    }

    public set items(items: RadioButtonItem[]) {

        if (!Array.isArray(items)) {
            return;
        }
        items = ArrayHelper.unique(items);
        if (items.length < 1) {
            return;
        }

        const oldValue = this._items;
        this._items = items;
        this.requestUpdate(`items`, oldValue);
    }

    /**
     * Extra markup rendered inside `.wrapper`, below the radio items — e.g.
     * the concrete picker for whichever option is currently selected. This
     * component is Light DOM (no shadow root, see
     * `AbstractLitElement.createRenderRoot`), so a native `<slot>` would be
     * inert here; a caller-supplied template via this property is how
     * follow-up content stays hierarchically nested under the radios it
     * belongs to instead of sitting as an unrelated sibling in the caller's
     * own template.
     */
    @property({type: Object})
    public content: HTMLTemplateResult | null = null;

    @state()
    private _value: T | null = null;

    @property({type: Object})
    public get value(): T | null {
        return this._value;
    }

    public set value(value: T) {
        if (value !== null && !this._items.some(item => item.value === value)) {
            return;
        }

        if (this._value === value) {
            return;
        }

        this._value = value;
    }

    protected render(): unknown {
        return html`
            ${when(this.label, () => html`
                <label>${this.label}</label>
            `)}
            <div class="wrapper">
                ${when(
                        ArrayHelper.notEmpty(this._items),
                        () => repeat(
                                this._items,
                                state => state.id,
                                state => this._renderItem(state)
                        )
                )}
                ${this.content}
            </div>
            ${this._renderHelperText()}
            ${this._renderErrors()}
        `;
    }

    private _isChecked(value: unknown): boolean {
        return this._value === value;
    }

    private _renderItem(item: RadioButtonItem): HTMLTemplateResult {
        const id = `${this._id}-${item.id}`;
        // `data-item` names the option for CSS (e.g. a per-option icon in dialogs.css); the
        // input's own id carries a per-instance uuid and is not meant to be styled.
        return html`
            <ha-input-radio data-item=${item.id}>
                <input id=${id}
                       name=${this._id}
                       type="radio"
                       ?readonly=${this.readonly}
                       .checked=${this._isChecked(item.value)}
                       @input=${() => this._onInput(item)}>
                ${when(item.label, () => html`
                    <label for=${id}>${item.label}</label>
                `)}
            </ha-input-radio>
            ${when(!!item.helpText, () => html`
                <label for=${id}
                       class="input-helptext">${item.helpText}
                </label>
            `)}
        `;
    }

    private _onInput(newValue: RadioButtonItem<T>): void {
        if (this.readonly) {
            return;
        }

        if (this._value === newValue.value) {
            return;
        }

        this.value = newValue.value;

        this.fire(this.value);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ha-input-radio-group': HaInputRadioGroupComponent;
    }
}